import type { Query } from "@Light/components/app/QueryLoader";
import { createContext, useContext } from "react";
import type {
  EnrollmentScreen,
  Requirements,
  RequirementsPatch,
} from "./types";
import { EnrollRoute } from "@Light/components/enroll/routes.ts";

export type FlowMutation = Query & {
  error?: unknown;
  reset?: () => void;
};

export type UseEnrollment = {
  screens: EnrollmentScreen[];
  nav: { stack: string[]; cursor: number };
  requirements: Requirements;
  initialRequirements: Requirements;
  finalized: boolean;
  nextMutation: FlowMutation;
  saveMutation: FlowMutation;
  saveAndFinalizeMutation: FlowMutation;
  /**
   * PATCHes requirements without advancing to the next screen. Errors do not
   * throw; a failure resolves to `undefined` and is surfaced via `saveMutation`.
   */
  save: (payload: RequirementsPatch) => Promise<Requirements | undefined>;
  /**
   * Moves to the next screen and optionally PATCHes requirements to the account.
   *
   * By default errors do not throw and are handled automatically, but you can set
   * `throwOnError: true` to make them throw. */
  next: (
    payload?: RequirementsPatch,
    opts?: {
      replace?: boolean;
      throwOnError?: boolean;
      returnTo?: EnrollRoute;
    },
  ) => Promise<void>;
  back: (() => void) | undefined;
  /**
   * PATCHes requirements and then finalizes enrollment. Resets its underlying
   * mutations first so a retry starts clean; tracked by `saveAndFinalizeMutation`.
   */
  saveAndFinalize: (payload: RequirementsPatch) => Promise<void>;
  /**
   * Navigates to an arbitrary screen in the flow. Prefer `next`, unless you
   * need to deviate from the flow's linear path.
   *
   * Pushes a history entry by default; `opts.replace` swaps the current one
   * instead. Replacing also drops the current screen from the flow's visited
   * stack, unless `opts.keepInStack` says to leave it there.
   *
   * Navigating to an already-visited screen moves the cursor back to it and
   * keeps the screens after it in the stack, so `next` replays them rather than
   * jumping to the end (e.g. editing a field from the confirmation screen).
   * `opts.keepInStack` opts out, pushing a fresh entry so `next` advances
   * forward instead (e.g. jumping to a re-quoted rate ahead in the stack).
   */
  navigate: (
    path: string,
    opts?: { replace?: boolean; keepInStack?: boolean; returnTo?: EnrollRoute },
  ) => void;
  /** Resolves a flow-relative path to a full route, e.g. for link hrefs. */
  href: (path: string) => string;
};

export const EnrollmentContext = createContext<UseEnrollment | undefined>(
  undefined,
);

export function useEnrollment(): UseEnrollment {
  const enrollment = useContext(EnrollmentContext);
  if (!enrollment) {
    throw new Error("useEnrollment must be used within an <EnrollmentFlow>");
  }
  return enrollment;
}
